import { Form, Formik } from 'formik';
import * as bd from 'react-basic-design';
import { CountryDTO } from '../../../api/country-api';
import { Region, RepresentativeDTO } from '../../../api/representative-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDelete from '../../../assets/icons/Delete';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { T, translate } from '../../../components/basic/text';
import { yup } from '../../../components/basic/yup';
import { FormikTextField } from '../../../components/formik';
import { useState } from 'react';
import SvgAdd from '../../../assets/icons/Add';

interface SaveRepresentativeModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedRepresentative?: RepresentativeDTO;
    countries: CountryDTO[];
    onHide: () => void;
    onSave: (values: RepresentativeDTO) => void;
    onDelete?: (representative: RepresentativeDTO) => void;
    regions: Region[];
}

export function SaveRepresentativeModal({
    show,
    allowEdit,
    regions,
    selectedRepresentative,
    countries,
    onHide,
    onSave,
    onDelete,
}: SaveRepresentativeModalProps) {
    const [newServiceText, setNewServiceText] = useState('');
    const RepresentativeSchema = yup.object().shape({
        countryId: yup.string().required(' '),
        regionId: yup.string().required(' '),
        contact: yup.string().required(' '),
        email: yup.string().required(' '),
        businessHoursWeekdays: yup.string().required(' '),
        businessHoursWeekend: yup.string().required(' '),
    });

    const defaultValues: RepresentativeDTO = {
        id: selectedRepresentative?.id,
        countryId: selectedRepresentative ? selectedRepresentative.countryId : '',
        regionId: selectedRepresentative ? selectedRepresentative.regionId : '',
        contact: selectedRepresentative ? selectedRepresentative.contact : '',
        email: selectedRepresentative ? selectedRepresentative.email : '',
        title: selectedRepresentative ? selectedRepresentative.title : '',
        businessHoursWeekdays: selectedRepresentative
            ? selectedRepresentative.businessHoursWeekdays
            : '',
        businessHoursWeekend: selectedRepresentative
            ? selectedRepresentative.businessHoursWeekend
            : '',
        services: selectedRepresentative ? selectedRepresentative.services : '',
    };

    if (!show) return <></>;

    return (
        <Formik
            initialValues={defaultValues}
            validationSchema={RepresentativeSchema}
            onSubmit={onSave}
        >
            {formik => (
                <BasicModal
                    show={show}
                    onHide={onHide}
                    size="xl"
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <T>
                                {selectedRepresentative
                                    ? 'edit-representative'
                                    : 'new-representative'}
                            </T>
                            <bd.Flex align="center" gap={2}>
                                <bd.Button
                                    type="button"
                                    variant="icon"
                                    size="sm"
                                    onClick={formik.submitForm}
                                    disabled={!allowEdit}
                                >
                                    <SvgSave />
                                </bd.Button>
                                {selectedRepresentative && onDelete && (
                                    <bd.Button
                                        disabled={!allowEdit}
                                        variant="icon"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => onDelete(selectedRepresentative)}
                                    >
                                        <SvgDelete />
                                    </bd.Button>
                                )}
                                <bd.Button
                                    disabled={!allowEdit}
                                    variant="icon"
                                    color="secondary"
                                    size="sm"
                                    onClick={onHide}
                                >
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex align="center" wrap>
                            <FormikTextField
                                label="title"
                                name="title"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />
                            <FormikTextField
                                label="country-id"
                                name="countryId"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...countries.map(country => ({
                                        id: country.id,
                                        title: `${country.name} `,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="region"
                                name="regionId"
                                items={[
                                    { id: null, title: '' },
                                    ...regions.map(region => ({
                                        id: region.id,
                                        title: region.name,
                                    })),
                                ]}
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="contact"
                                name="contact"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="email"
                                name="email"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />
                        </bd.Flex>
                        <bd.Flex align="center" wrap>
                            <FormikTextField
                                label="business-hours-weekdays"
                                name="businessHoursWeekdays"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="business-hours-weekend"
                                name="businessHoursWeekend"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />
                        </bd.Flex>

                        <bd.FormRow label={translate('services')}>
                            <bd.Flex vertical gap={2}>
                                <bd.Flex align="center" gap={2}>
                                    <bd.TextField
                                        name="services"
                                        placeholder={translate('service-title')}
                                        value={newServiceText}
                                        readOnly={!allowEdit}
                                        width="11rem"
                                        onChange={e => setNewServiceText(e.target.value)}
                                        buttonIcon={allowEdit && newServiceText && <SvgAdd />}
                                        disabled={!allowEdit || !newServiceText}
                                        buttonOnClick={() => {
                                            if (!newServiceText) return;
                                            const newServices = [
                                                ...JSON.parse(formik.values.services || '[]'),
                                                newServiceText,
                                            ];
                                            formik.setFieldValue(
                                                'services',
                                                JSON.stringify(newServices)
                                            );
                                            setNewServiceText('');
                                        }}
                                    />
                                </bd.Flex>
                                <bd.List compact>
                                    {JSON.parse(formik.values.services || '[]').map(
                                        (service: string, index: number) => (
                                            <bd.ListItem
                                                compact
                                                style={{ maxWidth: '20rem' }}
                                                key={service}
                                                primary={
                                                    <span className="text-muted">
                                                        {index + 1}. {service}
                                                    </span>
                                                }
                                                alignTop
                                                controls={
                                                    <bd.Button
                                                        type="button"
                                                        variant="icon"
                                                        size="sm"
                                                        color="secondary"
                                                        onClick={() => {
                                                            const newServices = JSON.parse(
                                                                formik.values.services || '[]'
                                                            ).filter((s: string) => s !== service);
                                                            formik.setFieldValue(
                                                                'services',
                                                                JSON.stringify(newServices)
                                                            );
                                                        }}
                                                    >
                                                        <SvgDelete />
                                                    </bd.Button>
                                                }
                                            />
                                        )
                                    )}
                                </bd.List>
                            </bd.Flex>
                        </bd.FormRow>
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
}
